import { useCallback, useEffect, useRef, useState } from 'react';
import type * as React from 'react';

// Same elements jQuery UI's draggable() refuses to start a drag from by default
const CANCEL_SELECTOR = 'input, textarea, button, select, option';

// Lets the user drag a call popup out of the way, like the legacy dialer and
// incoming call prompt (jQuery UI draggable). Spread the result onto the element.
export const useDraggable = () => {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Ends the drag in progress, if any
  const stopDragRef = useRef<(() => void) | null>(null);

  // The popup can unmount mid-drag (call ended, ring expired)
  useEffect(() => () => stopDragRef.current?.(), []);

  const onMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button !== 0) return;
    if ((event.target as Element).closest(CANCEL_SELECTOR)) return;
    event.preventDefault();
    stopDragRef.current?.();

    const startX = event.clientX - offset.x;
    const startY = event.clientY - offset.y;
    const onMove = (move: MouseEvent) => setOffset({ x: move.clientX - startX, y: move.clientY - startY });
    const stopDrag = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', stopDrag);
      stopDragRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', stopDrag);
    stopDragRef.current = stopDrag;
  }, [offset]);

  return {
    onMouseDown,
    style: { transform: `translate(${offset.x}px, ${offset.y}px)` },
  };
};

// Click and keyboard (Enter/Space) handlers for the legacy-styled anchors and
// dialpad cells, which aren't natively focusable or keyboard operable
export const pressable = (onPress: () => void) => ({
  role: 'button',
  tabIndex: 0,
  onClick: onPress,
  onKeyDown: (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onPress();
  },
});
